import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  createUrlTreeFromSnapshot,
  DetachedRouteHandle,
  RouteReuseStrategy,
} from '@angular/router';

import { DetachableComponent, isDetachableComponent } from '../../components/detachable/detachable.component';

/**
 * This implementation of `RouteReuseStrategy` allows routes to specify that a component should be detached
 * and reused when navigating back based on data in the route. Two things must be true for the component
 * state to be saved:
 *   1. The route data specifies `allowReuse: true`
 *   2. The destination URL starts with a value in the data's `reuseUrls` array.
 *
 * The saved component instance will be stored in the `handles` Map, then when the route is revisited the
 * instance will be reused if the navigation event is coming from one of the allowed destination URLs.
 */
export class UrlRouteReuseStrategy implements RouteReuseStrategy {
  private handles = new Map<string, DetachedRouteHandle>();
  private sourceUrl: string | null = null;
  private destinationUrl: string | null = null;

  /**
   * Should the router just re-use the current component/configuration?
   * This is also the only point the strategy knows both the source and destination routes.
   * @param future The destination route, after navigation.
   * @param curr The route before navigation. This is the route we're answering the question for.
   */
  shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    // Store URLs for checking in `shouldDetach` and `shouldAttach`.
    this.destinationUrl = createUrlTreeFromSnapshot(future, []).toString();
    this.sourceUrl = createUrlTreeFromSnapshot(curr, []).toString();

    // Default angular behavior: only return true at this point if the destination route configuration is identical.
    // Our custom logic is in `shouldDetach` and `shouldAttach`.
    return future.routeConfig === curr.routeConfig;
  }

  /**
   * Determines whether the component instance should be preserved. Returns true when the route config
   * allows reuse and the destination URL (determined in `shouldReuseRoute`) matches one of the allowed URLs.
   * The match is a simple startsWith string comparison.
   * @param route The current route snapshot, before navigation.
   */
  shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return (
      !!route.routeConfig?.data &&
      route.routeConfig.data.allowReuse &&
      route.routeConfig.data.reuseUrls?.some((url: string) => this.destinationUrl?.startsWith(url))
    );
  }

  /**
   * Determines whether to attach a route from the cache. Returns true when the route data specifies the
   * URL we are coming from in its allowed reuse URLs, otherwise the cached instance should be discarded.
   * @param route The new destination route snapshot.
   */
  shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const key = this.getRouteKey(route);
    const handle = this.handles.get(key);
    if (handle) {
      if (isTreeNodeHandle(handle)) {
        const routeData = handle.route.value.snapshot.data;
        const shouldAttach = routeData.reuseUrls?.some((url: string) => this.sourceUrl?.startsWith(url));
        if (!shouldAttach) {
          // if we got here and the route should not be attached, clean it up and get new state.
          // destroyDetachedRouteHandle(handle); // Angular 21 gives us a way to explicitly destroy the component. For now count on GC.
          this.handles.delete(key);
        }
        return shouldAttach;
      }
      return false;
    } else {
      return false;
    }
  }

  /**
   * Fetches the `DetachedRouteHandle` from our cache.
   * When the return value is null, the component will be re-created.
   * @param route The new destination route snapshot.
   */
  retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const key = this.getRouteKey(route);
    const handle = this.handles.get(key);
    if (isDetachableComponentHandle(handle)) {
      handle.componentRef.instance.onAttach();
    }
    return handle ?? null;
  }

  /**
   * Stores the `handle` in our cache.
   * @param route The current route, before navigation.
   * @param handle The route handle to store for future re-use. If null, any stored handle should be deleted.
   */
  store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    const key = this.getRouteKey(route);
    if (!!handle) {
      this.handles.set(key, handle);
      if (isDetachableComponentHandle(handle)) {
        handle.componentRef.instance.onDetach();
      }
    } else {
      // destroyDetachedRouteHandle(handle); // Angular 21 gives us a way to explicitly destroy the component. For now count on GC.
      this.handles.delete(key);
    }
  }

  // Turns an `ActivatedRouteSnapshot` into an absolute URL. Ignores query params.
  private getRouteKey(route: ActivatedRouteSnapshot): string {
    const path = route.pathFromRoot
      .filter(u => u.url.length > 0)
      .map(u => u.url)
      .join('/');
    return `/${path}`;
  }
}

function isDetachableComponentHandle(
  handle: DetachedRouteHandle
): handle is { componentRef: { instance: DetachableComponent } } {
  const isComponentHandle = handle && typeof handle?.['componentRef']?.['instance'] === 'object';
  return isComponentHandle && isDetachableComponent(handle['componentRef']['instance']);
}

function isTreeNodeHandle(handle: DetachedRouteHandle): handle is { route: { value: ActivatedRoute } } {
  return (
    handle &&
    typeof handle?.['route']?.['value']?.['snapshot']?.['data'] === 'object' &&
    handle['route']['value']['snapshot']['data'] !== null
  );
}
